import { useEffect, useCallback } from "react";
import { Link, useT } from "@/i18n";
import { ArrowRight, Users, Maximize2, BedDouble, Star, Heart, MapPin, Clock, ExternalLink, X, ChevronLeft, ChevronRight, CalendarCheck } from "lucide-react";
import { has, fmtPrice, pickSeasonalImage, seasonalMainImage } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

export const Section = ({ children, className = "", id, testId }) => (
  <section id={id} data-testid={testId} className={`py-16 sm:py-24 ${className}`}><div className="container-site">{children}</div></section>
);

export const SectionHead = ({ eyebrow, title, text, align = "left", className = "" }) => (
  <div className={`max-w-2xl ${align === "center" ? "mx-auto text-center" : ""} ${className}`}>
    {has(eyebrow) && <p className="eyebrow mb-3">{eyebrow}</p>}
    {has(title) && <h2 className="h2 whitespace-pre-line">{title}</h2>}
    {has(text) && <p className="mt-4 text-charcoal/70 leading-relaxed">{text}</p>}
  </div>
);

export const Fact = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-charcoal/70"><Icon size={14} className="text-sage" /> {children}</span>
);

export function ApartmentCard({ apt, compact = false, highlightCapacity = false }) {
  const t = useT();
  const price = fmtPrice(apt.price_from);
  const cover = seasonalMainImage(apt);
  return (
    <article className="card group flex flex-col" data-testid={`apartment-card-${apt.slug}`}>
      <Link to={`/appartements/${apt.slug}`} className="block aspect-[4/3] overflow-hidden bg-cream">
        {cover?.url && <img src={cover.url} alt={cover.alt || apt.images?.[0]?.alt || apt.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />}
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="h3">{apt.name}</h3>
        {highlightCapacity && has(apt.capacity) && (
          <p className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-forest" data-testid={`apartment-capacity-${apt.slug}`}>
            <Users size={15} className="text-sage" /> {t("jusqu'à {n} personnes", { n: apt.capacity })}
          </p>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
          {!highlightCapacity && has(apt.capacity) && <Fact icon={Users}>{t("{n} pers.", { n: apt.capacity })}</Fact>}
          {has(apt.surface) && <Fact icon={Maximize2}>{apt.surface} m²</Fact>}
          {has(apt.bedrooms_count) && <Fact icon={BedDouble}>{t("{n} ch.", { n: apt.bedrooms_count })}</Fact>}
          {has(apt.beds_count) && <Fact icon={BedDouble}>{t("{n} couchages", { n: apt.beds_count })}</Fact>}
        </div>
        {!compact && has(apt.short_description) && <p className="mt-4 text-sm text-charcoal/70 leading-relaxed line-clamp-3">{apt.short_description}</p>}
        {!compact && has(apt.essentials) && (
          <ul className="mt-4 flex flex-wrap gap-2">{apt.essentials.slice(0, 4).map((e, i) => <li key={i} className="text-[11px] px-2.5 py-1 rounded-full bg-sage-100 text-forest">{e.label}</li>)}</ul>
        )}
        <div className="mt-auto pt-5 flex items-center justify-between gap-3">
          {price ? <p className="text-sm font-semibold text-forest" data-testid={`apartment-price-${apt.slug}`}>{t("À partir de {price}", { price })} <span className="font-normal text-charcoal/60">/ {apt.price_unit || t("semaine")}</span></p> : <span />}
          <Link to={`/appartements/${apt.slug}`} data-testid={`apartment-btn-detail-${apt.slug}`} className="btn-primary px-5 py-2.5 text-xs">{compact ? t("Découvrir") : t("Voir l'appartement")} <ArrowRight size={14} /></Link>
        </div>
      </div>
    </article>
  );
}

export function Stars({ n }) {
  const t = useT();
  if (!has(n)) return null;
  return <span className="inline-flex gap-0.5" aria-label={t("{n} sur 5", { n })}>{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} className={i <= n ? "fill-gold text-gold" : "text-sand"} />)}</span>;
}

export function ReviewCard({ r, apartments = [] }) {
  const apt = apartments.find((a) => a.id === r.apartment_id);
  return (
    <article className="card p-6 flex flex-col" data-testid={`review-card-${r.id}`}>
      <Stars n={r.rating} />
      <p className="mt-4 font-serif text-lg leading-snug text-forest flex-1">« {r.comment} »</p>
      <footer className="mt-5 flex items-center gap-3 text-xs text-charcoal/60">
        {r.photo_url && <img src={r.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" loading="lazy" />}
        <div><p className="font-semibold text-forest">{r.first_name}</p><p>{[r.stay_date, apt?.name, r.source].filter(has).join(" · ")}</p></div>
      </footer>
    </article>
  );
}

export function PoiCard({ p, onOpen }) {
  const t = useT();
  return (
    <article className="card group flex flex-col" data-testid={`poi-card-${p.id}`}>
      {p.image_url && <div className="aspect-[16/10] overflow-hidden bg-cream"><img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" /></div>}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="eyebrow">{t(p.category)}</p>
          {p.favorite && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gold bg-gold-100 px-2.5 py-1 rounded-full" data-testid="poi-badge-badouille-favorite"><Heart size={11} className="fill-gold" /> {t("Coup de cœur de Badouille")}</span>}
        </div>
        <h3 className="font-serif text-xl text-forest mt-2">{p.name}</h3>
        {has(p.short_description) && <p className="text-sm text-charcoal/70 mt-2 leading-relaxed">{p.short_description}</p>}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
          {has(p.distance) && <Fact icon={MapPin}>{p.distance}</Fact>}
          {has(p.travel_time) && <Fact icon={Clock}>{p.travel_time}</Fact>}
        </div>
        <div className="mt-auto pt-4 flex gap-3">
          {onOpen && <button onClick={() => onOpen(p)} className="text-sm font-medium text-forest underline-offset-4 hover:underline" data-testid={`poi-open-${p.id}`}>{t("En savoir plus")}</button>}
          {has(p.link) && <a href={p.link} target="_blank" rel="noreferrer" className="text-sm text-sage inline-flex items-center gap-1 hover:text-forest"><ExternalLink size={13} /> {t("Site")}</a>}
        </div>
      </div>
    </article>
  );
}

export function Lightbox({ items, index, onClose, onIndex }) {
  const t = useT();
  const next = useCallback(() => onIndex((index + 1) % items.length), [index, items.length, onIndex]);
  const prev = useCallback(() => onIndex((index - 1 + items.length) % items.length), [index, items.length, onIndex]);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [next, prev, onClose]);
  if (index === null || !items[index]) return null;
  const it = items[index];
  return (
    <div className="fixed inset-0 z-[100] bg-forest-950/95 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t("Photo en plein écran")} data-testid="lightbox" onClick={onClose}>
      <button onClick={onClose} aria-label={t("Fermer")} data-testid="lightbox-close" className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-ecru flex items-center justify-center hover:bg-white/20"><X size={20} /></button>
      {items.length > 1 && <>
        <button onClick={(e) => { e.stopPropagation(); prev(); }} aria-label={t("Photo précédente")} data-testid="lightbox-prev" className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 text-ecru flex items-center justify-center hover:bg-white/20"><ChevronLeft size={22} /></button>
        <button onClick={(e) => { e.stopPropagation(); next(); }} aria-label={t("Photo suivante")} data-testid="lightbox-next" className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 text-ecru flex items-center justify-center hover:bg-white/20"><ChevronRight size={22} /></button>
      </>}
      <figure className="max-w-6xl max-h-full" onClick={(e) => e.stopPropagation()}>
        <img src={it.url} alt={it.alt || it.title || ""} className="max-h-[80vh] w-auto max-w-full object-contain rounded-xl" />
        {(it.title || it.alt) && <figcaption className="text-center text-ecru/70 text-sm mt-3">{it.title || it.alt} <span className="text-ecru/40">· {index + 1}/{items.length}</span></figcaption>}
      </figure>
    </div>
  );
}

export function CtaBanner({ title, text, image, to = "/demande-de-disponibilite", btnLabel }) {
  const { content } = useSite();
  const tt = useT();
  const b = content.cta_final || {};
  const img = image || pickSeasonalImage(b);
  const t = title || b.title || tt("Envie de séjourner au Chalet Badouille Izoard ?");
  const tx = text === undefined ? b.text : text;
  return (
    <section className="relative py-24 sm:py-32 text-center text-ecru overflow-hidden" data-testid="cta-final">
      {img && <img src={img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" data-testid="cta-final-image" />}
      <div className="absolute inset-0 bg-forest-950/75" />
      <div className="relative container-site max-w-2xl mx-auto">
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl">{t}</h2>
        {has(tx) && <p className="mt-4 text-ecru/80">{tx}</p>}
        <Link to={to} data-testid="cta-final-btn" className="btn-light mt-8 px-8 py-4"><CalendarCheck size={18} /> {btnLabel || tt("Demander une disponibilité")}</Link>
      </div>
    </section>
  );
}

export const PageHero = ({ eyebrow, title, text, image, testId }) => (
  <section className="relative bg-forest text-ecru overflow-hidden" data-testid={testId}>
    {image && <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" loading="eager" />}
    <div className="relative container-site py-20 sm:py-28 max-w-3xl">
      {has(eyebrow) && <p className="eyebrow text-gold-400 mb-3">{eyebrow}</p>}
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{title}</h1>
      {has(text) && <p className="mt-5 text-ecru/80 text-base sm:text-lg max-w-2xl leading-relaxed">{text}</p>}
    </div>
  </section>
);

export const Empty = ({ children, testId }) => (
  <div className="rounded-2xl border border-dashed border-sand bg-white/50 p-10 text-center text-sm text-charcoal/60" data-testid={testId}>{children}</div>
);
